import { GetEphemeralAssetChainResponse, getProvider, ITypes, protocolService } from '@optimex-xyz/trade-wallet-sdk'

import { RefundParams } from '@/strategies'
import { AcrossPayload, BridgeType, InputDataPresubmitTrade, IToken, MetaData } from '@/types'
import { encodeAffiliateData, getAffiliateData } from '@/utils/aff'
import { EphemeralWalletSigner } from '@/utils/EphemeralWalletSigner'
import { WalletContextType } from '@/wallets'

import { AbiCoder, ethers, sha256, toUtf8String } from 'ethers'

import { ensureHexPrefix, getMessageToTrade } from './trade'
import { isValidSolanaAddress } from './validations'

const abiCoder = AbiCoder.defaultAbiCoder()

export const l2Decode = (info: string) => {
  try {
    return toUtf8String(info)
  } catch {
    return info
  }
}

export function getTradeId(sessionId: string, solverAddress: string, tradeInfo: ITypes.TradeInfoStruct): string {
  const encodedData: string = abiCoder.encode(
    ['uint256', 'address', 'tuple(uint256,bytes[3],bytes[3])'],
    [BigInt(sessionId), solverAddress, [tradeInfo.amountIn, tradeInfo.fromChain, tradeInfo.toChain]]
  )

  return ensureHexPrefix(sha256(encodedData))
}

export const signTradeMessage = async ({
  wallet,
  tradeId,
  toUserAddress,
}: {
  wallet: WalletContextType
  tradeId: string
  toUserAddress: string
}): Promise<EphemeralWalletSigner> => {
  try {
    const normalizedRecipientAddress = normalizeRecipientAddress(toUserAddress)
    const messageToSign = getMessageToTrade({
      tradeId,
      toUserAddress: normalizedRecipientAddress,
      fromAddress: wallet.address,
    })
    const base64Signature = await wallet.signMessage(messageToSign)
    const hexSignature = convertBase64ToHex(base64Signature)
    return new EphemeralWalletSigner(hexSignature)
  } catch (error) {
    throw new Error(`Failed to sign trade message: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Normalizes the recipient address based on network type
 * Solana addresses are kept as-is, others are converted to lowercase
 */
export const normalizeRecipientAddress = (address: string): string => {
  const trimmedAddress = address.trim()
  return isValidSolanaAddress(trimmedAddress) ? trimmedAddress : trimmedAddress.toLowerCase()
}

const convertBase64ToHex = (base64Signature: string): string => {
  return Buffer.from(base64Signature, 'base64').toString('hex')
}

export const signDepositMessage = async ({ wallet, tradeId }: { wallet: WalletContextType; tradeId: string }) => {
  return await signTradeMessage({ wallet, tradeId: tradeId, toUserAddress: '' })
}

export const getUserEphemeralL2Address = async ({
  request,
  wallet,
  tradeId,
  fromToken,
  toUserAddress,
}: {
  request: InputDataPresubmitTrade
  wallet: WalletContextType
  tradeId: string
  fromToken: IToken
  toUserAddress: string
}) => {
  let userEphemeralL2Address = request.userEphemeralL2Address
  let userEphemeralAssetChainPubkey =
    request.userEphemeralAssetChainPubkey || ((request as any).ephemeralAssetChainKey as string)

  const mpcAssetChainPubkey = request.mpcAssetChainPubkey || ((request as any).mpcPubkey as string)
  if (!mpcAssetChainPubkey) throw new Error('mpcAssetChainPubkey not found')

  const creatorPublicKey = await wallet.getCompressedPublicKey()

  if (!userEphemeralL2Address || !userEphemeralAssetChainPubkey) {
    const signer = await signTradeMessage({ wallet, tradeId, toUserAddress })
    const assetChainWallet = await signer.getEphemeralAssetChain()
    const l2ConfigWallet = await signer.getEphemeralL2Config()

    userEphemeralL2Address = l2ConfigWallet.address
    userEphemeralAssetChainPubkey = getAssetChainPubkey(fromToken, assetChainWallet)
  }
  return { userEphemeralL2Address, userEphemeralAssetChainPubkey, mpcAssetChainPubkey, creatorPublicKey }
}

async function decodeTransactionData(depositTxId: string, networkId: string) {
  const abi = [
    {
      inputs: [
        {
          internalType: 'address',
          name: 'ephemeralL2Address',
          type: 'address',
        },
        {
          components: [
            {
              internalType: 'uint256',
              name: 'sessionId',
              type: 'uint256',
            },
            {
              internalType: 'address',
              name: 'solver',
              type: 'address',
            },
            {
              components: [
                {
                  internalType: 'uint256',
                  name: 'amountIn',
                  type: 'uint256',
                },
                {
                  internalType: 'bytes[3]',
                  name: 'fromChain',
                  type: 'bytes[3]',
                },
                {
                  internalType: 'bytes[3]',
                  name: 'toChain',
                  type: 'bytes[3]',
                },
              ],
              internalType: 'struct ITypes.TradeInfo',
              name: 'tradeInfo',
              type: 'tuple',
            },
          ],
          internalType: 'struct IBaseVault.TradeInput',
          name: 'input',
          type: 'tuple',
        },
        {
          components: [
            {
              internalType: 'uint256',
              name: 'amount',
              type: 'uint256',
            },
            {
              internalType: 'uint64',
              name: 'timeout',
              type: 'uint64',
            },
            {
              internalType: 'address',
              name: 'mpc',
              type: 'address',
            },
            {
              internalType: 'address',
              name: 'ephemeralAssetAddress',
              type: 'address',
            },
            {
              internalType: 'address',
              name: 'refundAddress',
              type: 'address',
            },
          ],
          internalType: 'struct IBaseVault.TradeDetail',
          name: 'data',
          type: 'tuple',
        },
      ],
      name: 'deposit',
      outputs: [],
      stateMutability: 'payable',
      type: 'function',
    },
  ]
  const provider = getProvider(networkId)
  const tx = await provider.getTransaction(depositTxId)
  if (!tx) throw new Error('depositTxId not found ' + depositTxId)
  const interface2 = new ethers.Interface(abi)
  const decodedData = interface2.parseTransaction({ data: tx.data })
  if (!decodedData) throw new Error('can not decode depositTxId ' + depositTxId)
  return decodedData?.args
}

export async function buildTradeDataFromOnChain({ request, wallet, toUserAddress }: RefundParams) {
  const { depositTxId, tradeId, fromToken } = request

  const decodedData = await decodeTransactionData(depositTxId, fromToken.networkId)
  const sessionId = '0x' + decodedData[1][0].toString(16)
  const amountIn = decodedData[2][0]
  const scriptTimeout = decodedData[2][1]

  const { userEphemeralAssetChainPubkey, userEphemeralL2Address, mpcAssetChainPubkey, creatorPublicKey } =
    await getUserEphemeralL2Address({
      request,
      wallet,
      tradeId,
      fromToken,
      toUserAddress,
    })

  return {
    amountIn,
    scriptTimeout,
    sessionId,
    userEphemeralAssetChainPubkey,
    mpcAssetChainPubkey,
    creatorPublicKey,
    userEphemeralL2Address,
  }
}

export function getAssetChainPubkey(fromToken: IToken, assetChainWallet: GetEphemeralAssetChainResponse): string {
  let userEphemeralAssetChainPubkey = ''
  switch (fromToken.networkType.toUpperCase()) {
    case 'EVM':
      userEphemeralAssetChainPubkey = assetChainWallet.evmPublicKey
      break
    case 'BTC':
      userEphemeralAssetChainPubkey = assetChainWallet.btcPublicKey
      break
    case 'SOLANA':
      userEphemeralAssetChainPubkey = assetChainWallet.solanaPublicKey
      break

    default:
      break
  }

  return userEphemeralAssetChainPubkey
}

export const getL2Config = async ({
  signer,
  fromToken,
  acrossPayload,
  metadata,
  bridgeType,
}: {
  signer: EphemeralWalletSigner
  fromToken: IToken
  acrossPayload?: AcrossPayload
  metadata?: MetaData
  bridgeType?: BridgeType
}) => {
  const mpcAssetChainPubkey = l2Decode((await protocolService.getCurrentPubkey(fromToken.networkId)).mpcAssetPubkey)
  const affiliateInfo = encodeAffiliateData(getAffiliateData(acrossPayload, metadata), bridgeType)

  const assetChainWallet = await signer.getEphemeralAssetChain()
  const l2ConfigWallet = await signer.getEphemeralL2Config()

  return {
    mpcAssetChainPubkey,
    affiliateInfo,
    assetChainWallet,
    l2ConfigWallet,
  }
}
export function stringToHex(str: string): string {
  if (!str) return ''
  const utf8Bytes = ethers.toUtf8Bytes(str)
  const paddedArray = new Uint8Array(32)
  paddedArray.set(utf8Bytes)
  return ethers.hexlify(paddedArray)
}
